// @flow
import React, { Component } from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  Icon,
  Select,
  Row,
  Col,
  Form,
  Input,
  Card,
  Button,
  Alert,
  Tooltip,
} from 'grab-ui';

import { hasJustSuccess, isRequestsPending } from 'utils/request-state-utils';
import Toastr from 'utils/toastr-util';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import DatePickerWrapper from 'omega-rtc/components/form/ucd/common/DatePickerWrapper';
import {
  extractCampaignIDFromURL,
  getTimezoneNameFromAbbrv,
  getRequestStateKey,
  generateCampaignCode,
} from 'omega-rtc/utils/ucd-utils';

import {
  createUCDCampaign,
  getUCDCampaign,
  getUCDProject,
  getUCDConfigs,
  updateUCDCampaign,
  GET_UCD_CAMPAIGN,
  GET_UCD_PROJECT,
  CREATE_UCD_CAMPAIGN,
  UPDATE_UCD_CAMPAIGN,
} from 'omega-rtc/actions/ucd';
import UCDStatusLabel from 'omega-rtc/components/form/ucd/common/UCDStatusLabel';
import {
  BUDGET_SOURCE_DISPLAY_NAMES,
  CHANNEL_DISPLAY_NAMES,
  CHANNEL_GROUP_CHANNELS,
  UCD_ROUTES_LINK,
  VERTICALS_DISPLAY_NAMES,
  INTERNAL_STATUS,
  UCD_RESOURCE_NAMES,
  UCD_ACTION_TYPES,
  CHANNEL_GROUP_DISPLAY_NAMES,
  VERTICALS_TO_IGNORED,
} from 'omega-rtc/constants/ucd';

import AddCampaignTypeForm from 'omega-rtc/components/form/ucd/AddCampaignTypeForm';
import UCDCampaignCodeFormItem, {
  CAMPAIGN_CODE_MODES,
} from 'omega-rtc/components/form/ucd/common/UCDCampaignCodeFormItem';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';

import './UCDHomePage.scss';
import './CampaignDetail.scss';
import MetricsAvailable from 'img/ucd_campaign_code_right.svg';
import MetricsNotAvailable from 'img/ucd_metrics_not_available.svg';
import { getTimezoneOptions } from 'omega-rtc/utils/time-utils';

class CampaignEditPage extends Component {
  constructor(props: Props) {
    super(props);
    const {
      projectID,
      campaignID,
      getCampaign,
      getProject,
      location,
      getConfigs,
    } = this.props;
    getConfigs();
    if (!campaignID) {
      const dupID = _.get(location, 'query.campaignToDuplicate', '');
      this.state = {
        isDuplicate: !!dupID,
        duplicateID: dupID,
      };
      getCampaign(projectID, dupID);
    } else {
      this.state = {
        isDuplicate: false,
      };
      getCampaign(projectID, campaignID);
    }
    getProject(projectID);
  }

  componentWillReceiveProps(nextProps: Props) {
    const { projectID, campaignID, form } = this.props;
    const formValues = form.getFieldsValue();
    if (
      hasJustSuccess(
        this.props.getCampaignRequest,
        nextProps.getCampaignRequest,
      )
    ) {
      this.props.form.setFieldsValue({
        campaignCode: nextProps.campaignView.campaign.code,
      });
    }

    // created campaign from duplicate
    if (
      hasJustSuccess(
        this.props.createCampaignRequest,
        nextProps.createCampaignRequest,
      )
    ) {
      Toastr.success(`${formValues.name} has been successfully created!`);
      this.props.router.push(
        UCD_ROUTES_LINK.CAMPAIGN_VIEW(projectID, nextProps.newCampaignID),
      );
    }

    // updated existing campaign
    if (
      hasJustSuccess(
        this.props.updateCampaignRequest,
        nextProps.updateCampaignRequest,
      )
    ) {
      Toastr.success('Campaign has been successfully updated!');
      this.props.router.push(
        UCD_ROUTES_LINK.CAMPAIGN_VIEW(projectID, campaignID),
      );
    }
  }

  // shouldDateBeDisabled returns true if the current date is beyond the project date range
  shouldDateBeDisabled = (currentDate, timezone) => {
    if (!currentDate || !moment.isMoment(currentDate)) {
      return true;
    }

    const { project } = this.props.projectView;
    const { start, end } = project;

    const projectStartDate = moment.tz(start, timezone).startOf('day');
    const projectEndDate = moment.tz(end, timezone).endOf('day');
    return (
      currentDate &&
      (currentDate < projectStartDate || currentDate > projectEndDate)
    );
  };

  // getCampaignGeneratedCode will try to generate code using form value, if form value not ready, it generates code from the original campaign
  getCampaignGeneratedCode(form, campaignView, isDuplicate) {
    const commonFields = {
      countryCode: _.get(
        _.find(this.props.countries, ['countryID', campaignView.countryID]),
        'code',
      ),
      audience: campaignView.audience,
      objective: campaignView.objective,
    };
    const { getFieldsValue } = form;
    const formValues = getFieldsValue();
    if (!_.isEmpty(formValues)) {
      const { startDate, shortName, vertical, budgetType } = formValues;
      if (startDate && shortName && vertical && budgetType) {
        return generateCampaignCode({
          ...commonFields,
          year: startDate.year(),
          shortName,
          vertical,
          budgetType,
        });
      }
    }

    if (isDuplicate) {
      // duplicate must use the form values to auto generate
      return undefined;
    }

    return generateCampaignCode({
      ...commonFields,
      year: moment(campaignView.start).year(),
      shortName: campaignView.campaign.shortName,
      vertical: _.get(campaignView.campaign.verticals, 0, ''),
      budgetType: campaignView.campaign.budgetSource,
    });
  }

  onTimezoneChange = (form, timezone, countryTimezones) => {
    const currentStartDate = form.getFieldValue('startDate');
    const currentEndDate = form.getFieldValue('endDate');

    const timezoneName = getTimezoneNameFromAbbrv(timezone, countryTimezones);
    form.setFieldsValue({
      startDate: currentStartDate.tz(timezoneName),
      endDate: currentEndDate.tz(timezoneName),
    });
  };

  render() {
    const { router, configs, user, createCampaign, updateCampaign, countries } =
      this.props;

    const campaignView = _.get(this.props, 'campaignView', { campaign: {} });
    const projectView = _.get(this.props, 'projectView', { project: {} });
    const countryID = _.get(projectView, 'project.countryID');
    const timezones = _.get(
      _.find(countries, { countryID }, {}),
      'timezones',
      [],
    );
    const timezoneOptions = getTimezoneOptions(timezones);

    const isLoading =
      isRequestsPending(
        this.props.getCampaignRequest,
        this.props.getProjectRequest,
      ) ||
      !user ||
      !configs;

    const { isDuplicate, duplicateID } = this.state;
    const { form, projectID, campaignID } = this.props;
    const {
      name,
      shortName,
      channelGroup,
      channels,
      start,
      end,
      verticals,
      budgetSource,
      code,
      startTime,
      endTime,
      mamCampaignID,
      timezone,
      templateType,
      otherDetails,
      isPendingApproval,
    } = campaignView.campaign;
    const { createdBy, status } = campaignView;
    const { getFieldDecorator, getFieldValue } = form;
    const generatedCode = isLoading
      ? ''
      : this.getCampaignGeneratedCode(form, campaignView, isDuplicate);

    let autoGeneratedCode = generatedCode;
    if (isDuplicate && generatedCode === code) {
      // Reset code when duplicate & code has not changed
      autoGeneratedCode = undefined;
    }

    const channelTypeCurrentVal =
      form.getFieldValue('channelType') || channels || [];
    const statusUpper = _.toUpper(status);
    const isCampaignDeactivated =
      INTERNAL_STATUS.deactivated === statusUpper && !isDuplicate;

    let isCodeInitialModeAuto = isDuplicate || code === autoGeneratedCode;

    let initialCampaignCode = autoGeneratedCode;
    if (isCodeInitialModeAuto) {
      initialCampaignCode = isDuplicate ? undefined : code;
    }

    if (isCampaignDeactivated) {
      // Special handling
      isCodeInitialModeAuto = true;
      initialCampaignCode = code;
      autoGeneratedCode = code;
    }

    const timezoneAbbrv = form.getFieldValue('timezone');
    const timezoneName = getTimezoneNameFromAbbrv(timezoneAbbrv, timezones);

    const startTimeMoment = _.isEmpty(timezoneName)
      ? moment(start)
      : moment(startTime).tz(timezoneName);

    const endTimeMoment = _.isEmpty(timezoneName)
      ? moment(end)
      : moment(endTime).tz(timezoneName);

    const isTimeModificationNotAllowed =
      !isDuplicate && !_.isEmpty(mamCampaignID);
    const notAllowedTimeModificationText = `This field is only editable on the ${_.get(
      CHANNEL_GROUP_DISPLAY_NAMES,
      channelGroup,
    )} page`;

    const filteredVerticals = _.filter(
      _.get(configs, 'verticals'),
      (vertical) => !VERTICALS_TO_IGNORED.includes(vertical),
    );
    const filteredVerticalOptions = _.map(filteredVerticals, (vertical) => (
      <Select.Option key={vertical} value={vertical}>
        {_.get(VERTICALS_DISPLAY_NAMES, vertical)}
      </Select.Option>
    ));

    return (
      <div className={'ucd-basic'}>
        <ProgressiveContainer isLoading={isLoading}>
          <div className="campaign-page-header">
            <Link
              className="campaign-back-button-link"
              onClick={() =>
                this.props.router.push(
                  UCD_ROUTES_LINK.CAMPAIGN_VIEW(
                    projectID,
                    isDuplicate ? duplicateID : campaignID,
                  ),
                )
              }
            >
              <div className="campaign-page-header-back-button">
                <Icon type="grabicon-arrow-back-02" />
              </div>
              <div className="campaign-page-header-back-title">
                Back to Campaign Detail Page
              </div>
            </Link>
          </div>
          <div>
            <div
              className="campaign-page-title-row"
              style={{
                borderBottom: '1px #E8E8E8 solid',
                marginBottom: 0,
                paddingBottom: 32,
              }}
            >
              <div className="campaign-page-title-main">
                {isDuplicate ? 'New' : 'Edit'} Campaign
              </div>
            </div>

            <Form
              layout="vertical"
              style={isCampaignDeactivated ? { cursor: 'not-allowed' } : {}}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <div style={{ width: 783, paddingRight: 40, paddingTop: 40 }}>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item
                        className="sub-form-campaign-desc-item"
                        label="Campaign Description"
                      >
                        {getFieldDecorator('name', {
                          rules: [
                            {
                              required: true,
                              message:
                                'Please input your campaign description!',
                            },
                          ],
                          initialValue: isDuplicate ? `Copy of ${name}` : name,
                        })(
                          <Input
                            disabled={isCampaignDeactivated}
                            placeholder="Please input your campaign description"
                          />,
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <div
                    className="campaign-channel-group"
                    style={{ paddingTop: 0, paddingBottom: 12 }}
                  >
                    Channel Group
                  </div>
                  <Card>
                    <AddCampaignTypeForm
                      channelGroup={channelGroup}
                      hideOptions={['channelType', 'channelGroupTitle']}
                    />
                  </Card>
                  {/* <Row gutter={24}> -- TODO: Shudn't allow to change campaign URL as it will mess up the trident campaign perf metrics, Might need to enable back later for non trident campaign */}
                  {/*  <Col span={24}> */}
                  {/*    <Form.Item style={{ marginBottom: 20, marginTop: 16 }}> */}
                  {/*      {getFieldDecorator('link', { */}
                  {/*        rules: [ */}
                  {/*          { */}
                  {/*            message: 'Invalid link', */}
                  {/*            pattern: URL_REGEX, */}
                  {/*          }, */}
                  {/*        ], */}
                  {/*        initialValue: link, */}
                  {/*      })( */}
                  {/*        <Input */}
                  {/*          disabled={isCampaignDeactivated} */}
                  {/*          placeholder="Please input link" */}
                  {/*        />, */}
                  {/*      )} */}
                  {/*    </Form.Item> */}
                  {/*  </Col> */}
                  {/* </Row> */}
                  <div
                    className="campaign-page-detail-title"
                    style={{ marginTop: 32, marginBottom: 15 }}
                  >
                    Campaign Details
                  </div>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'Channel Type'}
                      >
                        {getFieldDecorator('channelType', {
                          rules: [
                            {
                              required: 'true',
                            },
                          ],
                          initialValue: channels,
                        })(
                          <Select
                            mode={'multiple'}
                            placeholder={'Select Channel Type'}
                            allowClear
                            disabled={isCampaignDeactivated}
                            maxTagCount={2}
                            maxTagPlaceholder={`+ ${
                              channelTypeCurrentVal.length - 2
                            } Selected`}
                          >
                            {_.get(
                              CHANNEL_GROUP_CHANNELS,
                              channelGroup,
                              [],
                            ).map((channel) => (
                              <Select.Option value={channel} key={channel}>
                                {_.get(CHANNEL_DISPLAY_NAMES, channel)}
                              </Select.Option>
                            ))}
                          </Select>,
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label="Campaign Name"
                        style={{ marginBottom: 24 }}
                      >
                        {getFieldDecorator('shortName', {
                          rules: [
                            {
                              required: true,
                              message:
                                'Must be unique alphanumeric text, less than 10 characters.',
                              pattern: '^[a-zA-Z0-9]{1,10}$',
                            },
                          ],
                          initialValue: isDuplicate ? undefined : shortName,
                        })(
                          <Input
                            disabled={isCampaignDeactivated}
                            placeholder="Please input your campaign name"
                          />,
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'Start Date'}
                      >
                        <Tooltip
                          title={
                            isTimeModificationNotAllowed
                              ? notAllowedTimeModificationText
                              : ''
                          }
                        >
                          {getFieldDecorator('startDate', {
                            rules: [
                              {
                                required: true,
                              },
                            ],
                            initialValue: startTimeMoment,
                          })(
                            <DatePickerWrapper
                              disabled={
                                isCampaignDeactivated ||
                                isTimeModificationNotAllowed
                              }
                              disabledDate={(current) =>
                                this.shouldDateBeDisabled(current, timezoneName)
                              }
                              style={{ width: '100%' }}
                              placeholder="Select Date Time"
                              showTime={{ format: 'HH:mm' }}
                              format={'YYYY-MM-DD HH:mm'}
                            />,
                          )}
                        </Tooltip>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'End Date'}
                      >
                        <Tooltip
                          title={
                            isTimeModificationNotAllowed
                              ? notAllowedTimeModificationText
                              : ''
                          }
                        >
                          {getFieldDecorator('endDate', {
                            rules: [
                              {
                                required: true,
                              },
                            ],
                            initialValue: endTimeMoment,
                          })(
                            <DatePickerWrapper
                              disabledDate={(current) => {
                                if (
                                  this.shouldDateBeDisabled(
                                    current,
                                    timezoneName,
                                  )
                                ) {
                                  return true;
                                }
                                const minDate = getFieldValue('startDate');
                                if (!minDate) {
                                  return false;
                                }
                                return minDate.diff(current) > 0;
                              }}
                              disabled={
                                isCampaignDeactivated ||
                                isTimeModificationNotAllowed
                              }
                              style={{ width: '100%' }}
                              placeholder="Select Date Time"
                              showTime={{ format: 'HH:mm' }}
                              format={'YYYY-MM-DD HH:mm'}
                            />,
                          )}
                        </Tooltip>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'Budget Type'}
                      >
                        {getFieldDecorator('budgetType', {
                          rules: [
                            {
                              required: true,
                            },
                          ],
                          initialValue: budgetSource,
                        })(
                          <Select
                            disabled={isCampaignDeactivated}
                            placeholder={'Select Budget Type'}
                          >
                            {_.map(
                              _.get(configs, 'budgetSources'),
                              (budgetSource) => (
                                <Select.Option
                                  key={budgetSource}
                                  value={budgetSource}
                                >
                                  {_.get(
                                    BUDGET_SOURCE_DISPLAY_NAMES,
                                    budgetSource,
                                  )}
                                </Select.Option>
                              ),
                            )}
                          </Select>,
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'Timezone'}
                      >
                        <Tooltip
                          title={
                            isTimeModificationNotAllowed
                              ? notAllowedTimeModificationText
                              : ''
                          }
                        >
                          {getFieldDecorator('timezone', {
                            rules: [
                              {
                                required: true,
                              },
                            ],
                            initialValue: timezone,
                          })(
                            <Select
                              placeholder={'Select Timezone'}
                              disabled={isTimeModificationNotAllowed}
                              onChange={(value) => {
                                this.onTimezoneChange(form, value, timezones);
                              }}
                            >
                              {_.map(timezoneOptions, (timezone) => (
                                <Select.Option
                                  key={timezone.value}
                                  value={timezone.value}
                                >
                                  {timezone.text}
                                </Select.Option>
                              ))}
                            </Select>,
                          )}
                        </Tooltip>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        className="sub-form-campaign-item"
                        label={'Vertical'}
                      >
                        {getFieldDecorator('vertical', {
                          rules: [
                            {
                              required: true,
                            },
                          ],
                          initialValue: verticals,
                        })(
                          <Select
                            disabled={isCampaignDeactivated}
                            placeholder={'Select Vertical'}
                          >
                            {filteredVerticalOptions}
                          </Select>,
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item>
                    {form.getFieldDecorator('campaignCode', {
                      rules: [
                        {
                          required: true,
                          message: 'Campaign code cannot be empty',
                        },
                      ],
                      initialValue: initialCampaignCode,
                    })(
                      <UCDCampaignCodeFormItem
                        initialMode={
                          isCodeInitialModeAuto
                            ? CAMPAIGN_CODE_MODES.auto
                            : CAMPAIGN_CODE_MODES.manual
                        }
                        initialCode={isDuplicate ? '' : code}
                        autoGeneratedCode={autoGeneratedCode}
                        onCodeChange={(code) =>
                          form.setFieldsValue({ campaignCode: code })
                        }
                      />,
                    )}
                  </Form.Item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginTop: 36,
                    }}
                  >
                    <div style={{ flex: 1 }} />
                    <div>
                      <Button
                        style={{
                          fontWeight: 'bold',
                          padding: '10px 20px',
                          marginRight: 8,
                        }}
                        disabled={isCampaignDeactivated}
                        type="default"
                        onClick={() =>
                          router.push(
                            UCD_ROUTES_LINK.CAMPAIGN_VIEW(
                              projectID,
                              isDuplicate ? duplicateID : campaignID,
                            ),
                          )
                        }
                      >
                        Cancel
                      </Button>
                      <ShowIfAuthorized
                        conditions={[
                          {
                            resource: UCD_RESOURCE_NAMES.campaign,
                            action: isDuplicate
                              ? UCD_ACTION_TYPES.create
                              : UCD_ACTION_TYPES.update,
                          },
                        ]}
                      >
                        <Button
                          style={{
                            fontWeight: 'bold',
                            padding: '10px 20px',
                          }}
                          disabled={isCampaignDeactivated}
                          loading={isRequestsPending(
                            this.props.createCampaignRequest,
                            this.props.updateCampaignRequest,
                          )}
                          type="primary"
                          onClick={(e) => {
                            if (e && e.preventDefault) {
                              e.preventDefault();
                            }

                            form.validateFields((err, values) => {
                              if (err) {
                                return;
                              }
                              const {
                                link,
                                shortName,
                                name,
                                channelType,
                                startDate,
                                endDate,
                                timezone,
                                budgetType,
                                vertical,
                                campaignCode,
                              } = values;
                              if (startDate.diff(endDate) > 0) {
                                Toastr.error('Start Date bigger than end date');
                                return;
                              }

                              const verticals = _.isArray(vertical)
                                ? vertical
                                : [vertical];

                              let mamCampaignID = '';
                              if (!_.isEmpty(link)) {
                                const { error = '', campaignID = '' } =
                                  extractCampaignIDFromURL(link, channelGroup);
                                if (error) {
                                  Toastr.error(
                                    `Fail to parse campaign link: ${error}`,
                                  );
                                  return;
                                }
                                mamCampaignID = campaignID;
                              }

                              const requestBody = {
                                campaign: {
                                  shortName,
                                  name,
                                  link,
                                  timezone,
                                  mamCampaignID,
                                  start: startDate.format('YYYY-MM-DD'),
                                  end: endDate.format('YYYY-MM-DD'),
                                  startTime: startDate.clone().utc(),
                                  endTime: endDate.clone().utc(),
                                  channels: channelType,
                                  budgetSource: budgetType,
                                  channelGroup,
                                  code: campaignCode,
                                  verticals,
                                  templateType,
                                  isPendingApproval,
                                  otherDetails,
                                },
                                status: isDuplicate
                                  ? INTERNAL_STATUS.draft
                                  : status,
                                user: user.email,
                              };
                              if (isDuplicate) {
                                createCampaign(requestBody, projectID);
                              } else {
                                updateCampaign(
                                  requestBody,
                                  projectID,
                                  campaignID,
                                );
                              }
                            });
                          }}
                        >
                          Save
                        </Button>
                      </ShowIfAuthorized>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    width: 1,
                    backgroundColor: '#E8E8E8',
                    height: 500,
                    marginRight: 43,
                  }}
                />
                {!isDuplicate && (
                  <div style={{ flex: 1 }}>
                    <div className={'campaign-page-other-detail'}>
                      Other Details
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item style={{ marginBottom: 16 }} label={'Status'}>
                        {getFieldDecorator('status')(
                          <UCDStatusLabel
                            status={status}
                            start={start}
                            end={end}
                            labelSize={'medium'}
                          />,
                        )}
                      </Form.Item>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: '18px',
                        }}
                      >
                        <img
                          src={
                            _.get(campaignView, 'isMetricsAvailable')
                              ? MetricsAvailable
                              : MetricsNotAvailable
                          }
                          height={'18px'}
                          width={'18px'}
                          alt={''}
                        />
                      </div>
                    </div>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item label="Project" style={{ marginBottom: 16 }}>
                          <Link to={UCD_ROUTES_LINK.PROJECT_VIEW(projectID)}>
                            <div
                              style={{
                                display: 'block',
                                fontSize: '16px',
                                lineHeight: '20px',
                              }}
                              className="campaign-channel-group-card-title-link"
                            >
                              {projectView.project.name}
                            </div>
                          </Link>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item style={{ marginBottom: 16 }} label="Creator">
                          <span>{createdBy}</span>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            </Form>
          </div>
        </ProgressiveContainer>
      </div>
    );
  }
}

const mapStateToProps = (state: StoreState, ownProps: Object) => {
  const userPermissions = state.app.user.userPermissions;
  const countries = state.countries.entities;
  return {
    projectID: ownProps.params.entityID,
    campaignID: ownProps.params.campaignID,
    countries,
    user: state.app.user,
    userPermissions,
    campaignView: state.ucd.campaignView,
    projectView: state.ucd.projectView,
    configs: state.ucd.configs,
    getCampaignRequest: _.get(
      state.requests,
      getRequestStateKey(GET_UCD_CAMPAIGN),
      {},
    ),
    getProjectRequest: _.get(
      state.requests,
      getRequestStateKey(GET_UCD_PROJECT),
      {},
    ),
    createCampaignRequest: _.get(
      state.requests,
      getRequestStateKey(CREATE_UCD_CAMPAIGN),
      {},
    ),
    updateCampaignRequest: _.get(
      state.requests,
      getRequestStateKey(UPDATE_UCD_CAMPAIGN),
      {},
    ),
    newCampaignID: _.get(state, 'ucd.newlyCreatedCampaign', ''),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCampaign: (projectID, campaignID) => {
    dispatch(getUCDCampaign(projectID, campaignID));
  },
  getProject: (projectID) => {
    dispatch(getUCDProject(projectID));
  },
  getConfigs: () => {
    dispatch(getUCDConfigs());
  },
  createCampaign: (data, projectID) => {
    dispatch(createUCDCampaign(data, projectID));
  },
  updateCampaign: (data, projectID, campaignID) => {
    dispatch(updateUCDCampaign(data, projectID, campaignID));
  },
});

const FormHOC = Form.create();
const CampaignEditPageAuthorized = connect(
  mapStateToProps,
  mapDispatchToProps,
)(FormHOC(CampaignEditPage));

// page-level access control
const CampaignEditPageComponent = (props: Props) => (
  <ShowIfAuthorized
    conditions={[
      {
        resource: UCD_RESOURCE_NAMES.campaign,
        action: UCD_ACTION_TYPES.create,
      },
      {
        resource: UCD_RESOURCE_NAMES.project,
        action: UCD_ACTION_TYPES.update,
      },
    ]}
    unauthorizedContent={
      <Alert
        message="You don't have edit access to Holos campaigns"
        showIcon
        type="error"
      />
    }
  >
    <CampaignEditPageAuthorized {...props} />
  </ShowIfAuthorized>
);

export default CampaignEditPageComponent;
